import { Heading } from '@gc-bridge/components/Heading';
import { IconType } from '@gc-bridge/components/Icons/constants';
import { LogoIcon } from '@gc-bridge/components/Icons/Logo';
import { rewriteRequestsPath } from '@gc-bridge/interceptors';
import { authProvider } from '@gc-bridge/services';
import { useCurrentUserStore } from '@gc-bridge/stores/currentUser';
import { AdditionalColors } from '@gc-bridge/theme/colors';
import {
  ComponentType,
  getEmbeddedComponents,
  isRemoteInMaintenanceMode,
  RemoteComponent,
} from '@gcportal/base';
import { BuildCircleOutlined, ErrorOutline } from '@mui/icons-material';
import { Box, CircularProgress, styled, Toolbar, Tooltip } from '@mui/material';
import React, { Suspense, useCallback, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

export const StyledToolbar = styled(Toolbar)(({ theme: { palette, breakpoints } }) => ({
  padding: 0,
  [breakpoints.up('sm')]: {
    padding: 0,
  },
  borderBottom: `2px solid ${palette.primary.main}`,
}));

export const LogoBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 10,
  paddingLeft: 150,
});

export const LogoHeading = styled(Heading)(({ theme: { palette } }) => ({
  color: palette.primary.main,
  textTransform: 'none',
  fontSize: '20px',
  fontWeight: 600,
}));

export const HeadingDivider = styled(Heading)(({ theme: { palette } }) => ({
  color: palette.primary.main,
  textTransform: 'none',
  fontSize: '18px',
  fontWeight: 600,
}));

export const GcBridgeHeading = styled(Heading)(() => ({
  color: AdditionalColors.SpanishGray,
  textTransform: 'none',
  fontSize: '18px',
  fontWeight: 600,
}));

export const EmbeddedHeaderComponents = styled(Box)({
  marginLeft: '15px',
});

export function GcBridgeToolbar() {
  const { user } = useCurrentUserStore();

  const headerComponents = useMemo(() => {
    const components = getEmbeddedComponents(ComponentType.Header);

    return components.map((component) => {
      const commonProps = {
        remoteDisplayName: component.remoteDisplayName,
        maintenance: component.maintenance,
        maintenanceBypassGroups: component.maintenanceBypassGroups,
        shouldPassAuthFunction: component.shouldPassAuthFunction,
      };

      if (component.lazy) {
        return {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          element: React.lazy(component.load as any),
          ...commonProps,
        };
      }
      component.load();
      return {
        element: component.element,
        ...commonProps,
      };
    });
  }, []);

  const renderComponent = useCallback(
    ({
      element: Element,
      remoteDisplayName,
      maintenance,
      maintenanceBypassGroups,
      shouldPassAuthFunction,
    }: Partial<RemoteComponent>) => {
      const remoteIsInMaintenance = isRemoteInMaintenanceMode(
        { maintenance, maintenanceBypassGroups },
        user?.groups
      );

      if (remoteIsInMaintenance) {
        return (
          <Tooltip
            key={remoteDisplayName}
            arrow
            title={`${remoteDisplayName} is temporarily unavailable`}
            slotProps={{ tooltip: { sx: { color: 'white' } } }}
          >
            <BuildCircleOutlined sx={{ color: 'secondary.main', cursor: 'help' }} />
          </Tooltip>
        );
      }

      return (
        <ErrorBoundary
          key={remoteDisplayName}
          fallback={
            <Tooltip
              arrow
              title={`Could not load content from ${remoteDisplayName}`}
              slotProps={{ tooltip: { sx: { color: 'white' } } }}
            >
              <ErrorOutline sx={{ color: 'error.main', cursor: 'help' }} />
            </Tooltip>
          }
        >
          <Suspense fallback={<CircularProgress size={20} />}>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-expect-error */}
            <Element
              rewriteRequestsPath={rewriteRequestsPath}
              {...(shouldPassAuthFunction ? { shellProvidedAuth: authProvider } : {})}
            />
          </Suspense>
        </ErrorBoundary>
      );
    },
    [user?.groups]
  );

  return (
    <StyledToolbar>
      <LogoBox>
        <LogoIcon variant={IconType.Dark} />
        <LogoHeading>GuyCarpenter</LogoHeading>
        <HeadingDivider>|</HeadingDivider>
        <GcBridgeHeading>GC Bridge</GcBridgeHeading>
      </LogoBox>
      <EmbeddedHeaderComponents>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          {headerComponents.map(renderComponent)}
        </Box>
      </EmbeddedHeaderComponents>
    </StyledToolbar>
  );
}
